import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { absoluteUrl } from "@/lib/api";

export default function MenuPdfBook({ pages = [], pdfUrl, hasCategoryTabs = false }) {
  const [page, setPage] = useState(0);
  const [turning, setTurning] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [direction, setDirection] = useState("next");
  const [targetPage, setTargetPage] = useState(null);
  const [mobileZoomed, setMobileZoomed] = useState(false);
  const [isSpread, setIsSpread] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches,
  );
  const touchStart = useRef(null);
  const mobileScale = useRef(1);
  const mobilePinchActive = useRef(false);
  const busy = useRef(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => {
      setIsSpread(media.matches);
      setPage((current) => media.matches ? current - (current % 2) : current);
    };
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const step = isSpread ? 2 : 1;
    [pages[page + step], pages[page + step + 1]].filter(Boolean).forEach((url) => {
      const image = new Image();
      image.src = absoluteUrl(url);
    });
  }, [page, pages, isSpread]);

  useEffect(() => {
    mobileScale.current = 1;
    mobilePinchActive.current = false;
    touchStart.current = null;
    setMobileZoomed(false);
  }, [page]);

  async function turn(nextPage) {
    if (busy.current || turning || preparing || nextPage < 0 || nextPage >= pages.length) return;
    busy.current = true;
    setPreparing(true);
    const urls = [pages[nextPage], isSpread ? pages[nextPage + 1] : null].filter(Boolean);
    await Promise.all(urls.map((url) => new Promise((resolve) => {
      const image = new Image();
      image.onload = async () => {
        try {
          await image.decode?.();
        } catch {
          // A completed load is enough when decode() is unavailable.
        }
        resolve();
      };
      image.onerror = resolve;
      image.src = absoluteUrl(url);
    })));
    setDirection(nextPage > page ? "next" : "previous");
    setTargetPage(nextPage);
    setPreparing(false);
    requestAnimationFrame(() => requestAnimationFrame(() => setTurning(true)));
  }

  function finishTurn() {
    if (targetPage === null) return;
    setPage(targetPage);
    setTargetPage(null);
    setTurning(false);
    busy.current = false;
  }

  function onTouchStart(event) {
    if (!isSpread && event.touches?.length > 1) {
      mobilePinchActive.current = true;
      touchStart.current = null;
      return;
    }
    if (!isSpread && mobileScale.current > 1.02) {
      touchStart.current = null;
      return;
    }
    const touch = event.changedTouches?.[0];
    if (touch) touchStart.current = { x: touch.clientX, y: touch.clientY };
  }

  function onTouchMove(event) {
    if (!isSpread && (event.touches?.length > 1 || mobileScale.current > 1.02)) {
      if (event.touches?.length > 1) mobilePinchActive.current = true;
      touchStart.current = null;
    }
  }

  function onTouchCancel() {
    touchStart.current = null;
    mobilePinchActive.current = false;
  }

  function onTouchEnd(event) {
    if (!isSpread && event.touches?.length > 0) return;
    if (!isSpread && (mobilePinchActive.current || mobileScale.current > 1.02)) {
      touchStart.current = null;
      mobilePinchActive.current = false;
      return;
    }
    const start = touchStart.current;
    touchStart.current = null;
    const touch = event.changedTouches?.[0];
    if (!start || !touch) return;
    const horizontalDistance = touch.clientX - start.x;
    const verticalDistance = touch.clientY - start.y;
    if (
      Math.abs(horizontalDistance) >= 55
      && Math.abs(horizontalDistance) > Math.abs(verticalDistance) * 1.4
    ) {
      const step = isSpread ? 2 : 1;
      turn(page + (horizontalDistance < 0 ? step : -step));
    }
  }

  if (!pages.length) return null;

  const currentPage = pages[page];
  const nextPage = targetPage === null ? currentPage : pages[targetPage];
  const rightPage = pages[page + 1];
  const targetRightPage = targetPage === null ? rightPage : pages[targetPage + 1];
  const step = isSpread ? 2 : 1;
  const canGoNext = page + step < pages.length;

  return (
    <section
      data-testid="menu-pdf-book"
      className={`mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-12 py-12 md:pb-12 lg:pb-12 ${
        hasCategoryTabs ? "md:pt-10 lg:pt-12" : "md:pt-32 lg:pt-40"
      }`}
    >
      <div className="flex md:hidden items-center justify-between gap-4 mb-6">
        <div>
          <div className="overline">Current menu</div>
          <div className="mt-2 text-xs text-muted-foreground">
            Page {page + 1} of {pages.length}
          </div>
        </div>
        <a
          href={absoluteUrl(pdfUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[0.68rem] uppercase tracking-[0.2em] text-primary hover:text-foreground transition-colors"
        >
          Open PDF
        </a>
      </div>

      <div className="relative mx-auto max-w-[760px] md:hidden" style={{ perspective: "2400px" }}>
        <TransformWrapper
          key={page}
          initialScale={1}
          minScale={1}
          maxScale={4}
          centerOnInit
          doubleClick={{ disabled: false, mode: "toggle", step: 1.8 }}
          pinch={{ step: 5 }}
          wheel={{ disabled: true }}
          panning={{ disabled: !mobileZoomed, velocityDisabled: true }}
          onTransformed={(_, state) => {
            mobileScale.current = state.scale;
            const zoomed = state.scale > 1.02;
            setMobileZoomed((current) => current === zoomed ? current : zoomed);
          }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent
                wrapperClass="!w-full !overflow-hidden"
                contentClass="!w-full"
              >
                <div
                  data-testid="menu-pdf-mobile-surface"
                  className="relative w-full bg-[#f6f0e6] border border-primary/30 shadow-[0_20px_55px_rgba(0,0,0,0.42)]"
                  style={{ aspectRatio: "595 / 842" }}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  onTouchCancel={onTouchCancel}
                >
                  <img
                    src={absoluteUrl(turning ? nextPage : currentPage)}
                    alt={`Nelum Dining menu — page ${(targetPage ?? page) + 1}`}
                    className="absolute inset-0 w-full h-full object-contain bg-[#f6f0e6]"
                  />

                  {turning && (
                    <div
                      className={`absolute inset-0 menu-turning-sheet menu-turning-sheet-mobile ${
                        direction === "next" ? "menu-turning-sheet-next" : "menu-turning-sheet-previous"
                      }`}
                      onAnimationEnd={finishTurn}
                    >
                      <div className="menu-page-face menu-page-front">
                        <img
                          src={absoluteUrl(currentPage)}
                          alt=""
                          aria-hidden="true"
                          className="w-full h-full object-contain bg-[#f6f0e6]"
                        />
                        <div className="menu-page-fold-shadow" />
                      </div>
                      <div className="menu-page-face menu-page-back">
                        <img
                          src={absoluteUrl(nextPage)}
                          alt=""
                          aria-hidden="true"
                          className="w-full h-full object-contain bg-[#f6f0e6]"
                        />
                        <div className="menu-page-back-shade" />
                      </div>
                    </div>
                  )}

                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      boxShadow: "inset 14px 0 22px -20px rgba(0,0,0,.65), inset -5px 0 12px -12px rgba(0,0,0,.35)",
                    }}
                  />
                  <div className="pointer-events-none absolute inset-y-0 left-0 w-[2px] bg-white/35" />
                </div>
              </TransformComponent>

              <div className="mt-4 flex items-center justify-between gap-3">
                <span className="text-[0.65rem] text-muted-foreground">
                  Pinch or double-tap to zoom
                </span>
                <div className="flex shrink-0 overflow-hidden border border-border bg-background text-foreground">
                  <button
                    type="button"
                    onClick={() => zoomOut()}
                    aria-label="Zoom out"
                    className="flex h-10 w-10 items-center justify-center border-r border-border active:bg-muted"
                  >
                    <ZoomOut size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => resetTransform()}
                    aria-label="Reset zoom"
                    className="flex h-10 min-w-10 items-center justify-center border-r border-border px-2 text-[0.62rem] font-semibold"
                  >
                    1×
                  </button>
                  <button
                    type="button"
                    onClick={() => zoomIn()}
                    aria-label="Zoom in"
                    className="flex h-10 w-10 items-center justify-center active:bg-muted"
                  >
                    <ZoomIn size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </TransformWrapper>
        <div className="absolute inset-3 -z-10 bg-black/30 blur-xl translate-y-4" aria-hidden="true" />
      </div>

      <div
        data-testid="menu-pdf-desktop-surface"
        className="relative mx-auto hidden md:block max-w-[1080px] menu-book"
        style={{
          perspective: "2800px",
          aspectRatio: "1190 / 842",
          width: "min(100%, calc((100vh - 180px) * 1190 / 842))",
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="absolute inset-x-8 inset-y-5 translate-y-7 bg-black/45 blur-2xl rounded-[45%]" />
        <div className="absolute -inset-2 bg-[#4b2b17] border border-primary/35 shadow-[0_28px_75px_rgba(0,0,0,.58)]" />

        <div className="absolute inset-0 grid grid-cols-2 bg-[#eee5d7]">
          <div className="relative overflow-hidden border-r border-black/20">
            <img
              src={absoluteUrl(turning && direction === "previous" ? nextPage : currentPage)}
              alt={`Nelum Dining menu — page ${(turning && direction === "previous" ? targetPage : page) + 1}`}
              className="h-full w-full object-contain bg-[#f6f0e6]"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/15" />
          </div>
          <div className="relative overflow-hidden">
            {rightPage ? (
              <img
                src={absoluteUrl(turning && direction === "next" ? targetRightPage : rightPage)}
                alt={`Nelum Dining menu — page ${page + 2}`}
                className="h-full w-full object-contain bg-[#f6f0e6]"
              />
            ) : (
              <div className="h-full bg-[#f6f0e6]" />
            )}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-black/14" />
          </div>
        </div>

        {turning && direction === "next" && rightPage && (
          <div
            className="absolute inset-y-0 right-0 w-1/2 menu-turning-sheet menu-turning-sheet-next"
            onAnimationEnd={finishTurn}
          >
            <div className="menu-page-face menu-page-front">
              <img src={absoluteUrl(rightPage)} alt="" className="h-full w-full object-contain" />
              <div className="menu-page-fold-shadow" />
            </div>
            <div className="menu-page-face menu-page-back">
              <img src={absoluteUrl(nextPage)} alt="" className="h-full w-full object-contain" />
              <div className="menu-page-back-shade" />
            </div>
          </div>
        )}

        {turning && direction === "previous" && (
          <div
            className="absolute inset-y-0 left-0 w-1/2 menu-turning-sheet menu-turning-sheet-previous"
            onAnimationEnd={finishTurn}
          >
            <div className="menu-page-face menu-page-front">
              <img src={absoluteUrl(currentPage)} alt="" className="h-full w-full object-contain" />
              <div className="menu-page-fold-shadow" />
            </div>
            <div className="menu-page-face menu-page-back">
              <img src={absoluteUrl(targetRightPage)} alt="" className="h-full w-full object-contain" />
              <div className="menu-page-back-shade" />
            </div>
          </div>
        )}

        <div className="pointer-events-none absolute left-1/2 inset-y-0 z-20 w-8 -translate-x-1/2 bg-gradient-to-r from-black/20 via-white/10 to-black/20 shadow-[0_0_18px_rgba(0,0,0,.28)]" />
        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/15" />
        <div className="pointer-events-none absolute left-4 top-4 z-40 whitespace-nowrap rounded-full border border-white/25 bg-black/60 px-4 py-2 text-center text-[0.62rem] uppercase tracking-[0.16em] text-white/90 shadow-lg backdrop-blur-md">
          <span className="font-semibold text-primary-foreground">
            Pages {String(page + 1).padStart(2, "0")}
            {rightPage ? `–${String(page + 2).padStart(2, "0")}` : ""}
          </span>
          <span className="mx-2 text-white/40">/</span>
          <span>{String(pages.length).padStart(2, "0")}</span>
        </div>

        <button
          type="button"
          data-testid="menu-pdf-desktop-previous"
          onClick={() => turn(page - step)}
          disabled={page === 0 || turning || preparing}
          aria-label="Turn to the previous menu pages"
          className="group absolute inset-y-0 left-0 z-30 w-1/2 cursor-w-resize disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        >
          <span className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black/25 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100" />
          <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-black/35 text-white opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0">
            <ChevronLeft size={20} strokeWidth={1.5} />
          </span>
        </button>

        <button
          type="button"
          data-testid="menu-pdf-desktop-next"
          onClick={() => turn(page + step)}
          disabled={!canGoNext || turning || preparing}
          aria-label="Turn to the next menu pages"
          className="group absolute inset-y-0 right-0 z-30 w-1/2 cursor-e-resize disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        >
          <span className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-black/25 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100" />
          <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-black/35 text-white opacity-0 translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0">
            <ChevronRight size={20} strokeWidth={1.5} />
          </span>
        </button>
      </div>

      <div className="mt-7 flex md:hidden items-center justify-center gap-4 sm:gap-8">
        <button
          type="button"
          data-testid="menu-pdf-previous"
          onClick={() => turn(page - step)}
          disabled={page === 0 || turning || preparing}
          className="inline-flex items-center gap-2 px-4 py-3 border border-border text-[0.68rem] uppercase tracking-[0.18em] text-foreground hover:border-primary hover:text-primary disabled:opacity-35 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={14} strokeWidth={1.5} />
          Previous
        </button>
        <span className="text-[0.68rem] uppercase tracking-[0.18em] text-muted-foreground tabular-nums">
          {isSpread && rightPage
            ? `${String(page + 1).padStart(2, "0")}–${String(page + 2).padStart(2, "0")}`
            : String(page + 1).padStart(2, "0")} / {String(pages.length).padStart(2, "0")}
        </span>
        <button
          type="button"
          data-testid="menu-pdf-next"
          onClick={() => turn(page + step)}
          disabled={!canGoNext || turning || preparing}
          className="inline-flex items-center gap-2 px-4 py-3 border border-border text-[0.68rem] uppercase tracking-[0.18em] text-foreground hover:border-primary hover:text-primary disabled:opacity-35 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <ChevronRight size={14} strokeWidth={1.5} />
        </button>
      </div>
    </section>
  );
}